/**
 * SignupPage — Screen 2 of the auth flow (design: 05-auth.md, 39b-google-signin-ui.md).
 *
 * Route: /signup?return=<path>
 *
 * Accessibility:
 *   TC-012: All inputs have associated <label> elements.
 *   TC-013: On submit failure, focus moves to the first field with an error.
 *   TC-014: Password visibility toggle is keyboard-accessible.
 *
 * Validation:
 *   - Email: validated on blur (format check); re-validated on submit.
 *   - Password: validated on blur (min 8 chars); re-validated on submit.
 *   - On 409 (duplicate email): error adjacent to email field.
 *
 * OAuth (39b):
 *   - AC-2: "Continue with Google" button above email/password form.
 *   - AC-10: return URL passed through to Google initiation endpoint.
 */

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthCard } from './AuthCard';
import { EmailField } from './EmailField';
import { PasswordField } from './PasswordField';
import { GoogleSignInButton } from './GoogleSignInButton';
import { signup as signupApi, AuthApiError } from '../../api/auth';

const MIN_PASSWORD_LEN = 8;

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('return') ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  function validateEmail(): boolean {
    if (!email.trim()) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address.');
      return false;
    }
    setEmailError('');
    return true;
  }

  function validatePassword(): boolean {
    if (password.length < MIN_PASSWORD_LEN) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LEN} characters.`);
      return false;
    }
    setPasswordError('');
    return true;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const emailOk = validateEmail();
    const passwordOk = validatePassword();

    if (!emailOk || !passwordOk) {
      // TC-013: focus on the first field with an error.
      const firstErrorId = !emailOk ? 'signup-email' : 'signup-password';
      const el = document.getElementById(firstErrorId);
      if (el instanceof HTMLInputElement) el.focus();
      return;
    }

    setSubmitting(true);
    try {
      await signupApi(email, password);
      navigate(returnTo, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.status === 409) {
          setEmailError('An account with this email already exists. Log in?');
          const emailInput = document.getElementById('signup-email');
          if (emailInput instanceof HTMLInputElement) emailInput.focus();
        } else if (err.status === 422) {
          setPasswordError(err.message);
          const pwInput = document.getElementById('signup-password');
          if (pwInput instanceof HTMLInputElement) pwInput.focus();
        } else {
          setEmailError('Something went wrong. Please try again.');
        }
      } else {
        setEmailError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard>
      <h1 className="text-lg font-bold text-gray-900 mb-6">Create account</h1>

      {/* AC-2 (39b): Google button above the email/password form. */}
      <GoogleSignInButton returnTo={returnTo} />

      {/* "or" divider (design: 39b-google-signin-ui.md). */}
      <div className="flex items-center gap-3 my-4">
        <div className="flex-1 border-t border-hairline" />
        <span className="text-xs text-gray-400">or</span>
        <div className="flex-1 border-t border-hairline" />
      </div>

      <form onSubmit={handleSubmit} noValidate>
        <EmailField
          id="signup-email"
          label="Email"
          value={email}
          onChange={setEmail}
          onBlur={validateEmail}
          error={emailError}
          autoComplete="email"
        />

        <PasswordField
          id="signup-password"
          label="Password"
          value={password}
          onChange={setPassword}
          onBlur={validatePassword}
          error={passwordError}
          helperText="Minimum 8 characters"
          autoComplete="new-password"
        />

        <button
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] rounded-lg hover:bg-brand-primary-dk focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2 motion-safe:transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>

      <p className="mt-4 text-sm text-center text-gray-500">
        Already have an account?{' '}
        <Link
          to={`/login${returnTo !== '/' ? `?return=${encodeURIComponent(returnTo)}` : ''}`}
          className="text-brand-primary hover:underline font-medium"
        >
          Log in
        </Link>
      </p>
    </AuthCard>
  );
}
